/**
 * @fileoverview Helper para generar archivos Excel del Informe de Reservas de Inventario
 */

import * as XLSX from 'xlsx-js-style';
import { saveAs } from 'file-saver';
import { formatProductoNombreForExcel } from '../utils/productoInventarioHelper';

/**
 * Genera un archivo Excel con las reservas de inventario filtradas
 * @param {Array} reservas - Array de reservas de reserva_inventario (con expand de orden_produccion, producto_inventario, usuario)
 * @returns {void}
 */
export function generateExcel(reservas) {
  try {
    if (!reservas || reservas.length === 0) {
      throw new Error('No hay reservas para exportar');
    }

    // Definir headers
    const headers = [
      'Fecha de Creación',
      'Número de Orden',
      'Producto de Inventario',
      'Total',
      'Usuario',
      'Tipo',
      'Completado',
      'Inventario Local',
      'Comentario'
    ];

    // Transformar datos al formato requerido
    const rows = reservas.map(reserva => {
      // Fecha de creación
      const fechaCreacion = reserva.created 
        ? new Date(reserva.created).toLocaleString('es-ES', { 
            year: 'numeric', 
            month: '2-digit', 
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
          })
        : 'N/A';

      // Número de orden
      const numeroOrden = reserva.expand?.orden_produccion?.numero || 'N/A';

      // Producto de inventario (con medida concatenada)
      const productoInventario = formatProductoNombreForExcel(reserva.expand?.producto_inventario);

      // Total
      const total = reserva.total || 0;

      // Usuario
      const usuario = reserva.expand?.usuario?.name 
        || reserva.expand?.usuario?.email 
        || 'N/A';

      // Tipo (Devolución o Solicitud)
      const tipo = reserva.devolucion === true ? 'Devolución' : 'Solicitud';

      // Completado
      const completado = reserva.completado === true ? 'Sí' : 'No';

      // Inventario Local
      const inventarioLocal = reserva.inventario_local !== false ? 'Sí' : 'No';

      // Comentario
      const comentario = reserva.comentario || '';

      return [
        fechaCreacion,
        numeroOrden,
        productoInventario,
        total,
        usuario,
        tipo,
        completado,
        inventarioLocal,
        comentario
      ];
    });

    // Crear hoja de trabajo (array of arrays)
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);

    // Verificar que se creó correctamente
    if (!ws || !ws['!ref']) {
      throw new Error('Error al crear la hoja de trabajo Excel');
    }

    // Aplicar estilos y formato
    applyStyles(ws);

    // Crear libro de trabajo
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Reservas Inventario');

    // Generar archivo y descargar usando file-saver
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    const fileName = `Informe_Reservas_Inventario_${new Date().toISOString().split('T')[0]}.xlsx`;
    saveAs(blob, fileName);
  } catch (error) {
    console.error('Error generating Excel:', error);
    throw error;
  }
}

/**
 * Aplica estilos a la hoja de trabajo
 * @param {Object} ws - Hoja de trabajo
 */
function applyStyles(ws) {
  if (!ws || !ws['!ref']) {
    return; // No aplicar estilos si la hoja no es válida
  }

  const range = XLSX.utils.decode_range(ws['!ref']);
  const numRows = range.e.r + 1;
  const numCols = range.e.c + 1;

  // Aplicar estilos a todas las celdas
  for (let row = 0; row < numRows; row++) {
    for (let col = 0; col < numCols; col++) {
      const cellRef = XLSX.utils.encode_cell({ r: row, c: col });
      
      if (!ws[cellRef]) {
        continue; // Saltar celdas vacías
      }

      // Estilos para headers (primera fila)
      if (row === 0) {
        ws[cellRef].s = {
          font: { bold: true, color: { rgb: 'FFFFFF' } },
          fill: { fgColor: { rgb: '4472C4' } },
          alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
          border: {
            top: { style: 'thin', color: { rgb: '000000' } },
            bottom: { style: 'thin', color: { rgb: '000000' } },
            left: { style: 'thin', color: { rgb: '000000' } },
            right: { style: 'thin', color: { rgb: '000000' } }
          }
        };
      } else {
        // Estilos para datos
        ws[cellRef].s = {
          alignment: { horizontal: 'left', vertical: 'top', wrapText: true },
          border: {
            top: { style: 'thin', color: { rgb: 'CCCCCC' } },
            bottom: { style: 'thin', color: { rgb: 'CCCCCC' } },
            left: { style: 'thin', color: { rgb: 'CCCCCC' } },
            right: { style: 'thin', color: { rgb: 'CCCCCC' } }
          }
        };
      }
    }
  }

  // Configurar anchos de columna
  ws['!cols'] = [
    { wch: 20 }, // Fecha de Creación
    { wch: 15 }, // Número de Orden
    { wch: 30 }, // Producto de Inventario
    { wch: 10 }, // Total
    { wch: 25 }, // Usuario
    { wch: 12 }, // Tipo
    { wch: 12 }, // Completado
    { wch: 15 }, // Inventario Local
    { wch: 50 }  // Comentario
  ];

  // Configurar altura de filas y wrapText
  ws['!rows'] = [];
  for (let row = 0; row < numRows; row++) {
    ws['!rows'][row] = { hpt: row === 0 ? 30 : 20, hpx: row === 0 ? 30 : 20 };
  }
}

